import { Component } from "react";

class ErrorBoundary extends Component {
  state = { error: undefined, componentStack: "" };

  static getDerivedStateFromError(error) {
    return { error };
  }

  report = (error, componentStack = "") => {
    console.error("Remotor:", error, componentStack);
    this.setState({ error, componentStack });
  };

  onError = (event) => {
    if (
      !event.error &&
      ["ResizeObserver loop completed with undelivered notifications.", "ResizeObserver loop limit exceeded"].includes(
        event.message,
      )
    ) {
      console.warn("Remotor:", event.message);
      return;
    }
    this.report(event.error || new Error(`${event.message} (${event.filename}:${event.lineno}:${event.colno})`));
  };

  onRejection = (event) => this.report(event.reason instanceof Error ? event.reason : new Error(String(event.reason)));

  componentDidMount() {
    window.addEventListener("error", this.onError);
    window.addEventListener("unhandledrejection", this.onRejection);
  }

  componentWillUnmount() {
    window.removeEventListener("error", this.onError);
    window.removeEventListener("unhandledrejection", this.onRejection);
  }

  componentDidCatch(error, info) {
    this.report(error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <main className="flex min-h-dvh items-center justify-center bg-zinc-950 px-5 py-12 text-white">
        <section
          role="alert"
          className="w-full max-w-2xl rounded-2xl border border-white/15 bg-white/5 p-6 shadow-xl sm:p-8"
        >
          <p className="mb-2 text-sm font-medium text-stone-300">Remotor</p>
          <h1 className="text-2xl font-semibold">Something went wrong</h1>
          <p className="mt-3 text-stone-300">
            This screen could not continue. Reload to open it again. If the problem returns, share the error details
            below.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-5 rounded-lg bg-white px-5 py-3 font-medium text-stone-900 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
          >
            Reload Remotor
          </button>
          <details className="mt-6 rounded-lg border border-white/15">
            <summary className="cursor-pointer px-4 py-3 font-medium">Exact error and stack trace</summary>
            <p className="break-words border-t border-white/15 p-4 text-sm">
              {this.state.error.message || String(this.state.error)}
            </p>
            <pre className="max-h-[50dvh] overflow-auto whitespace-pre-wrap break-words border-t border-white/15 p-4 text-xs leading-relaxed text-stone-300">{`${this.state.error.stack || String(this.state.error)}${this.state.componentStack ? `\n\nReact component stack:${this.state.componentStack}` : ""}`}</pre>
          </details>
        </section>
      </main>
    );
  }
}

export default ErrorBoundary;
